import S, { DataSignal } from 's-js';
import { lift } from 's-array';
import { AssertExtends } from 'lib/assertExtends';

import { PortCall } from 'data/models/PortCall';

import { InPortOps, NewReservationInPortOpsParams } from 'portcall/inportops/model';
import { PortCallScheduleHeader } from 'portcall/portcallschedule/header';

import { AddPortCallTabModel } from './model';

export interface AddPortCallBerthsTabParams {
    PortCall : PortCall;
    ScheduleHeader : PortCallScheduleHeader;
}

export type AddPortCallBerthsTab = AssertExtends<ReturnType<typeof AddPortCallBerthsTab>, AddPortCallTabModel>;
export function AddPortCallBerthsTab(
    newReservationInPortOpsModel : (params : NewReservationInPortOpsParams) => InPortOps,
    {
        PortCall,
        ScheduleHeader
    } : AddPortCallBerthsTabParams
) {
    const
        Reservations = lift(() => PortCall.Reservations()),
        PortOps = Reservations
            .map(br => {
                const ops = newReservationInPortOpsModel({ Reservation: br });
                // If we don't have enough data to enter NEW cargo activities, then also clear any EXISTING
                // cargo activities. E.g. the user might've entered cargo activities and then gone back and
                // changed to a berth w/ no scheduling authority.
                S(() => !ops.HasRequiredCargoActivityLookups() && br.CargoActivities([]));
                return ops;
            }),
        CanProceed = PortOps.every(ops => ops.IsValidForSave());

    // A wrinkle: Changing location can change the scheduling authority, which can invalidate existing
    // CargoActivities' reference list values. Rather than trying to morph values from one scheduling
    // authority's reference list into those from another, we'll say that we'll clear any column that
    // currently references an item that isn't from the current scheduling authority's lists.
    Reservations.map(br => {
        // Optimization: Use an S.value() for schedAuthId so that we only need to run through every
        // CargoActivity when the schedAuthId is actually changing, as opposed to on every location change.
        const schedAuthId = S.value<number | null | undefined>(undefined);

        // DON'T coalesce undefined to null when setting schedAuthId. We wanna distinguish null (known berth
        // whose SchedulingAuthorityId is null) from undefined (unknown berth).
        S(() => schedAuthId(br.Itinerary()?.Location()?.Berth()?.SchedulingAuthorityId()));

        br.CargoActivities.map(cargo => {
            watch(cargo.ActivityType , cargo.ActivityTypeId );
            watch(cargo.CargoCategory, cargo.CargoCategoryId);
            watch(cargo.TradeRoute   , cargo.TradeRouteId   );

            function watch<T extends { OrganizationId : () => number }>(
                navProp : () => T | null,
                fkProp : DataSignal<number | null>
            ) {
                S(() => {
                    const refListItem = navProp();
                    if (
                        refListItem
                        // We have a known berth...
                     && schedAuthId() !== undefined
                        // ...whose scheduling authority doesn't match this item's org.
                     && schedAuthId() !== refListItem.OrganizationId()
                    ) {
                        // Clear the corresponding FK property
                        fkProp(null);
                    }
                });
            }
        });
    });

    return {
        ScheduleHeader,
        PortOps,
        CanProceed
    };
}



// WEBPACK FOOTER //
// ./apps/portcall/addportcall/berths.ts